<template>
  <div
    v-tippy="tooltip"
    :style="{ textAlign: store.textAlign }"
    class="supplement-text-section"
    :class="{ 'no-print': !displayValue }"
  >
    <div
      class="supplement-text-section-label-wrapper no-print"
      :class="{ 'is-visible': showLabel, 'is-ready': transitionsEnabled }"
    >
      <span class="supplement-text-section-label">{{ title }}</span>
    </div>
    <div class="supplement-text-field-row no-print">
      <GrowingTiptap
        v-model="displayValue"
        wrapper-class="control is-expanded"
        :wrapper-style="{ textTransform: store.textTransform }"
        class="supplement-text-section-input"
        :text-bold="shouldBoldText"
        :prefix-bold="shouldBoldLabel"
        :placeholder="placeholder"
        :data-test-id="testId"
        @blur="handleBlurAndSave"
        @update:text-bold="handleTextBoldChange"
        @update:prefix-bold="handlePrefixBoldChange"
      />
    </div>
    <!-- eslint-disable vue/no-v-html -- XSS mitigated: renderMarkdown escapes HTML before processing markdown -->
    <div
      v-if="hasPrintableContent"
      class="supplement-text-print-only supplement-text-section-text"
      :style="{ textTransform: store.textTransform }"
      v-html="renderedHtml"
    ></div>
    <!-- eslint-enable vue/no-v-html -->
  </div>
</template>

<script setup>
  import { computed, inject, ref, watch, onMounted } from "vue";
  import { directive as vTippy } from "vue-tippy";
  import { useSupplementLabelStore } from "../stores/supplement-label-store";
  import {
    escapeHtml,
    renderMarkdown,
    BARE_PREFIX_PATTERN,
    stripMarkdownMarkersForPrefix,
    findPrefixEndIndex,
    hasRealContent,
  } from "./utils/formatting";
  import GrowingTiptap from "../growing-tiptap.vue";

  const props = defineProps({
    title: { type: String, required: true },
    prefix: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    rows: { type: [Number, String], default: 1 },
    testId: { type: String, default: "" },
    fieldType: { type: String, required: true },
    defaultValueKey: { type: String, default: "" },
    boldLabelKey: { type: String, default: "" },
    boldTextKey: { type: String, default: "" },
    tooltip: { type: Object, default: null },
    autoAddPrefix: { type: Boolean, default: true },
  });

  const store = useSupplementLabelStore();
  const triggerSave = inject("triggerSave");

  // Prevent label flash on initial load
  const ready = ref(false);
  const transitionsEnabled = ref(false);
  onMounted(() => {
    ready.value = true;
    // Enable transitions after initial render completes
    requestAnimationFrame(() => {
      transitionsEnabled.value = true;
    });
  });

  const normalizedPrefix = computed(() => {
    if (!props.prefix) return "";
    const trimmed = props.prefix.trimEnd();
    return trimmed + " ";
  });

  const boldedPrefix = computed(() => {
    if (!normalizedPrefix.value) return "";
    const trimmed = normalizedPrefix.value.trimEnd();
    return `${trimmed} `;
  });

  function hasPrefix(text) {
    if (!text) return false;
    const stripped = stripMarkdownMarkersForPrefix(text);
    return BARE_PREFIX_PATTERN.test(stripped);
  }

  function extractCorePrefixText(text) {
    if (!text) return "";
    const stripped = stripMarkdownMarkersForPrefix(text);
    const match = stripped.match(BARE_PREFIX_PATTERN);
    if (!match) return "";
    return match[0].trim();
  }

  function stripDuplicatePrefix(text) {
    if (!text) return text;
    const prefixEnd = findPrefixEndIndex(text);
    if (prefixEnd === -1) return text;

    const rest = text.slice(prefixEnd);

    if (hasPrefix(rest)) {
      const firstCore = extractCorePrefixText(text);
      const secondCore = extractCorePrefixText(rest);
      if (firstCore.toLowerCase() === secondCore.toLowerCase()) {
        return rest;
      }
    }
    return text;
  }

  function ensurePrefix(text, defaultPrefix) {
    if (!defaultPrefix) return text || "";
    if (!text) return defaultPrefix;
    if (hasPrefix(text)) return stripDuplicatePrefix(text);
    const result = defaultPrefix + text;
    return stripDuplicatePrefix(result);
  }

  const storeValue = computed({
    get: () => store[props.fieldType] || "",
    set: (val) => {
      store[props.fieldType] = val;
    },
  });

  const displayValue = ref("");
  let lastSyncedToStore = null;

  function syncDisplayFromStore() {
    const storedValue = storeValue.value;

    if (props.autoAddPrefix) {
      displayValue.value = ensurePrefix(storedValue, boldedPrefix.value);
    } else {
      if (!storedValue) {
        displayValue.value = boldedPrefix.value;
      } else {
        displayValue.value = stripDuplicatePrefix(storedValue);
      }
    }
  }

  onMounted(() => {
    syncDisplayFromStore();
  });

  watch(
    displayValue,
    (newVal) => {
      lastSyncedToStore = newVal;
      storeValue.value = newVal;
    },
    { flush: "post" }
  );

  watch(storeValue, (newVal) => {
    if (newVal === lastSyncedToStore) return;
    syncDisplayFromStore();
  });

  const shouldBoldLabel = computed(() => {
    if (!props.boldLabelKey) return true;
    return store.boldOptions?.[props.boldLabelKey] ?? true;
  });

  const shouldBoldText = computed(() => {
    if (!props.boldTextKey) return false;
    return store.boldOptions?.[props.boldTextKey] ?? false;
  });

  function stripMarkdownMarkers(str) {
    return str.replace(/\*\*/g, "").replace(/_/g, "");
  }

  const hasPrintableContent = computed(() => {
    const content = displayValue.value;
    if (!content) return false;
    const prefixEnd = findPrefixEndIndex(content);
    if (prefixEnd === -1) {
      // No prefix detected - check if there's any real content at all
      return hasRealContent(content);
    }
    // Has prefix - check if there's real content after it
    const rest = content.slice(prefixEnd);
    return hasRealContent(rest);
  });

  // Show label when field is empty or only has prefix.
  // For fields without required prefix (autoAddPrefix: false), also show label
  // when there's content but no prefix - the label clarifies what the content means.
  const showLabel = computed(() => {
    if (!ready.value) return false;
    if (!hasPrintableContent.value) return true;
    // Has content - for optional-prefix fields, show label if prefix is missing
    if (!props.autoAddPrefix && !hasPrefix(displayValue.value)) return true;
    return false;
  });

  function renderMarkdownWithPrefixBold(text) {
    if (!text) return "";

    const prefixEnd = findPrefixEndIndex(text);
    if (prefixEnd !== -1 && shouldBoldLabel.value) {
      const prefix = text.slice(0, prefixEnd);
      const rest = text.slice(prefixEnd);
      const cleanPrefix = stripMarkdownMarkers(prefix);
      return `<b>${escapeHtml(cleanPrefix)}</b>${renderMarkdown(rest)}`;
    }

    return renderMarkdown(text);
  }

  const renderedHtml = computed(() => {
    const content = displayValue.value;

    if (shouldBoldText.value) {
      const prefixEnd = findPrefixEndIndex(content);
      if (prefixEnd !== -1) {
        const prefix = content.slice(0, prefixEnd);
        const rest = content.slice(prefixEnd);
        if (hasRealContent(rest)) {
          const cleanPrefix = stripMarkdownMarkers(prefix);
          const prefixHtml = shouldBoldLabel.value
            ? `<b>${escapeHtml(cleanPrefix)}</b> `
            : escapeHtml(cleanPrefix) + " ";
          return prefixHtml + `<b>${renderMarkdown(rest)}</b>`;
        }
      } else if (content.trim()) {
        return `<b>${renderMarkdown(content)}</b>`;
      }
    }

    return renderMarkdownWithPrefixBold(content);
  });

  function handleTextBoldChange(allTextIsBold) {
    if (!props.boldTextKey) return;
    store.boldOptions = { ...store.boldOptions, [props.boldTextKey]: allTextIsBold };
  }

  function handlePrefixBoldChange(prefixIsBold) {
    if (!props.boldLabelKey) return;
    store.boldOptions = { ...store.boldOptions, [props.boldLabelKey]: prefixIsBold };
  }

  function handleBlurAndSave(ev, { changed } = {}) {
    let current = displayValue.value || "";

    current = stripDuplicatePrefix(current);

    const prefixWasAdded = props.autoAddPrefix && !hasPrefix(current) && boldedPrefix.value;
    if (prefixWasAdded) {
      current = current ? boldedPrefix.value + current : boldedPrefix.value;
    }

    const finalValue = stripDuplicatePrefix(current);
    displayValue.value = finalValue;

    // Directly update store before save to avoid race condition with flush: "post" watcher
    storeValue.value = finalValue;
    lastSyncedToStore = finalValue;

    if (changed || prefixWasAdded) {
      triggerSave?.();
    }
  }
</script>

<style lang="scss" scoped>
  @import "~/label/css/labels/supplements/supplement_mixins";

  .supplement-text-section {
    margin-top: 0.5rem;
  }

  .supplement-text-section-label-wrapper {
    display: grid;
    grid-template-rows: 0fr;

    &.is-ready {
      transition: grid-template-rows 0.2s ease-out;
    }

    &.is-visible {
      grid-template-rows: 1fr;
    }
  }

  .supplement-text-section-label {
    overflow: hidden;
    display: block;
    font-size: 11px;
    letter-spacing: 0.125em;
    text-transform: uppercase;
    color: #7a7a7a;
    margin-bottom: 2px;
  }

  .supplement-text-field-row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .control.is-expanded {
    flex: 1;
  }

  :deep([data-growing-tiptap]) {
    .growing-tiptap-input {
      :deep(.ProseMirror) {
        padding: 0 0.5rem 0.25rem 0;
        border-width: 0 1px 1px 0;
        border-style: solid dashed dashed solid;
        border-color: transparent $border-standard $border-standard transparent;
        border-radius: 0;
        background-color: transparent;

        &:focus,
        &.ProseMirror-focused {
          border-color: transparent $focus-outline-color $focus-outline-color transparent;
          box-shadow: none;
        }
      }
    }
  }

  .field-actions {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
    align-items: flex-start;
  }

  .supplement-text-print-only {
    display: none;
  }

  @media print {
    .supplement-text-print-only {
      display: block;
      white-space: pre-wrap;
    }
  }
</style>
